import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Paper,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import { API } from 'aws-amplify';
import { Link } from "react-router-dom";

const AdminProductList = () => {
  const [overlay, setOverlay] = useState(true);
  const [products, setProducts] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [customerCounts, setCustomerCounts] = useState([]);
  const [productsCount, setProductsCount] = useState(0);
  const [productsPerPage, setProductsPerPage] = useState(50);
  const [page, setPage] = useState(1);
  const [nextTokens, setNextTokens] = useState({ 1: null });

  const totalPages = Math.ceil(productsCount / productsPerPage);

  useEffect(() => {
    fetchTotalProducts();
  }, []);

  useEffect(() => {
    listProducts(page, productsPerPage);
  }, [page, productsPerPage]);

  const listProductsQuery = `
    query ListProducts($limit: Int, $nextToken: String) {
      listProducts(limit: $limit, nextToken: $nextToken) {
        items {
          id
          title
          price
          updatedAt
          sellerID
          disableFlag
        }
        nextToken
      }
    }
  `;

  const fetchCustomerCounts = async (productList) => {
    try {
      const res = await API.graphql({
        query: `
          query ListCustomers($limit: Int) {
            listCustomers(limit: $limit) {
              items {
                id
                productID
              }
            }
          }
        `,
        variables: { limit: 1000 },
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      });

      const items = res?.data?.listCustomers?.items || [];

      const countMap = items.reduce((acc, customer) => {
        const pid = customer.productID;
        acc[pid] = (acc[pid] || 0) + 1;
        return acc;
      }, {});

      return productList.map((p) => countMap[p.id] || 0);
    } catch (err) {
      return productList.map(() => 0);
    }
  };

  const fetchSellerNames = async (productList) => {
    const uniqueSellerIDs = [...new Set(productList.map(p => p.sellerID).filter(Boolean))];
    const sellerNameMap = {};

    for (const sellerID of uniqueSellerIDs) {
      try {
        const res = await API.graphql({
          query: `
            query GetSeller($id: ID!) {
              getSeller(id: $id) {
                name
              }
            }
          `,
          variables: { id: sellerID },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        });
        sellerNameMap[sellerID] = res.data.getSeller?.name || "不明な出品者";
      } catch {
        sellerNameMap[sellerID] = "不明な出品者";
      }
    }

    return productList.map(p => ({ name: sellerNameMap[p.sellerID] || "不明な出品者" }));
  };

  const fetchTotalProducts = async () => {
    let total = 0;
    let token = null;
    try {
      do {
        const variables = { limit: 100, nextToken: token };
        const response = await API.graphql({ query: listProductsQuery, variables, authMode: 'AMAZON_COGNITO_USER_POOLS' });
        const listData = response.data.listProducts;
        total += listData.items.length;
        token = listData.nextToken || null;
      } while (token);
      setProductsCount(total);
    } catch (error) {
      console.error("Error fetching total products:", error);
      setProductsCount(0);
    }
  };

  const listProducts = async (pageParam = 1, limit = productsPerPage) => {
    setOverlay(true);
    try {
      const variables = {
        limit,
        nextToken: nextTokens[pageParam] || null,
      };
      const response = await API.graphql({ query: listProductsQuery, variables, authMode: 'AMAZON_COGNITO_USER_POOLS' });
      const listData = response.data.listProducts;
      if (listData && listData.items) {
        setProducts(listData.items);
        const sellerNames = await fetchSellerNames(listData.items);
        setSellers(sellerNames);
        const customerCounts = await fetchCustomerCounts(listData.items);
        setCustomerCounts(customerCounts);
        setNextTokens(prev => ({
          ...prev,
          [pageParam + 1]: listData.nextToken || null,
        }));
      } else {
        setProducts([]);
        setSellers([]);
        setCustomerCounts([]);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setOverlay(false);
    }
  };

  const toggleProductStatus = async (product) => {
    try {
      const res = await API.graphql({
        query: `
          mutation UpdateProduct($input: UpdateProductInput!) {
            updateProduct(input: $input) {
              id
              disableFlag
            }
          }
        `,
        variables: {
          input: {
            id: product.id,
            disableFlag: !product.disableFlag,
          },
        },
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      });
  
      const updated = res.data.updateProduct;
  
      // 該当の商品の disableFlag をローカルstateだけ更新
      setProducts((prev) =>
        prev.map((p) =>
          p.id === updated.id ? { ...p, disableFlag: updated.disableFlag } : p
        )
      );
    } catch (err) {
      console.error("販売状態の更新失敗:", err);
    }
  };
  
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleProductsPerPageChange = async (event) => {
    const newLimit = event.target.value;
    setProductsPerPage(newLimit);
    setPage(1);
    setNextTokens({ 1: null });
    await fetchTotalProducts();
    listProducts(1, newLimit);
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleString();
  };

  return (
    <Box p={4}>
      {overlay ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="h4" mb={2}>商品一覧：{productsCount}件</Typography>

          <Box mb={3} display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">表示件数：</Typography>
            <FormControl size="small" variant="outlined">
              <InputLabel>件数</InputLabel>
              <Select
                value={productsPerPage}
                onChange={handleProductsPerPageChange}
                label="件数"
              >
                <MenuItem value={10}>10件</MenuItem>
                <MenuItem value={25}>25件</MenuItem>
                <MenuItem value={50}>50件</MenuItem>
                <MenuItem value={100}>100件</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>商品名</TableCell>
                  <TableCell>更新日</TableCell>
                  <TableCell>出品者</TableCell>
                  <TableCell>価格 (円)</TableCell>
                  <TableCell>顧客数</TableCell>
                  <TableCell>総売上 (円)</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product, index) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <Link to={`/products/${product.id}`} style={{ textDecoration: 'none', color: '#3f51b5' }}>
                        {product.title}
                      </Link>
                    </TableCell>
                    <TableCell>{formatDate(product.updatedAt)}</TableCell>
                    <TableCell>{sellers[index]?.name}</TableCell>
                    <TableCell>{product.price}</TableCell>
                    <TableCell>{customerCounts[index]} 人</TableCell>
                    <TableCell>{product.price * customerCounts[index]} 円</TableCell>
                    <TableCell>
                      <Button
                        color={product.disableFlag ? "primary" : "secondary"}
                        onClick={() => toggleProductStatus(product)}
                      >
                        {product.disableFlag ? "販売再開" : "販売停止"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box mt={4} display="flex" justifyContent="center">
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
            />
          </Box>

          <Box mt={4} display="flex" justifyContent="flex-start">
            <Button variant="contained" color="primary" href="/admin">
              管理画面トップに戻る
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default AdminProductList;
